<template>
  <section class="session-overview" aria-label="Dein Training im Überblick">
    <div v-if="metrics.length" class="session-overview__metrics">
      <div v-for="metric in metrics" :key="metric.key"><span>{{ metric.label }}</span><strong>{{ metric.value }}<small v-if="metric.unit">{{ metric.unit }}</small></strong></div>
    </div>
    <section v-if="hasPulse" class="session-overview__pulse" aria-label="Puls im Training">
      <header><h4>Puls im Training</h4><v-icon size="20" color="#ca919b">mdi-heart-pulse</v-icon></header>
      <div class="session-overview__pulse-values">
        <div v-for="metric in pulseMetrics" :key="metric.label"><span>{{ metric.label }}</span><strong>{{ number(metric.value) }} <small>BPM</small></strong></div>
      </div>
      <div v-if="plot.dots.length" class="session-overview__chart">
        <svg viewBox="0 0 320 76" role="img" aria-label="Pulsverlauf während des Trainings. Messlücken bleiben offen.">
          <line x1="4" y1="72" x2="316" y2="72" stroke="rgba(255,255,255,.08)" />
          <polyline v-for="(path, index) in plot.paths" :key="'l' + index" :points="path" />
          <circle v-for="(point, index) in plot.dots" :key="'p' + index" :cx="point.x" :cy="point.y" r="1.4" />
        </svg>
        <div class="session-overview__axis"><span>Start</span><span>Ende</span></div>
      </div>
      <p v-else class="session-overview__note">Für dieses Training ist eine Puls-Zusammenfassung verfügbar, aber kein vollständiger Verlauf.</p>
      <details v-if="$slots['pulse-details']" class="session-overview__details">
        <summary>Puls im Detail<v-icon size="18">mdi-chevron-down</v-icon></summary>
        <slot name="pulse-details" />
      </details>
    </section>
  </section>
</template>
<script>
import { healthPulsePlot } from "@/services/healthImportMatching"
const measured = value => typeof value === "number" && Number.isFinite(value) && value >= 0
export default {
  name: "WorkoutSessionOverview",
  props: {
    durationLabel: { type: String, default: "" }, energy: { type: Object, default: null },
    volume: { type: Number, default: null }, distance: { type: Number, default: null },
    recording: { type: Object, default: null }, pulse: { type: Object, default: null },
  },
  computed: {
    metrics() {
      const result = []
      if (this.durationLabel) result.push({ key: "duration", label: "Dauer", value: this.durationLabel })
      if (measured(this.volume)) result.push({ key: "volume", label: "Volumen", value: this.number(this.volume), unit: "kg" })
      if (this.energy) result.push({ key: "energy", label: this.energy.label, value: this.number(this.energy.value), unit: "kcal" })
      if (measured(this.distance) && this.distance > 0) result.push({ key: "distance", label: "Distanz", value: (this.distance / 1000).toLocaleString("de-CH", { maximumFractionDigits: 2 }), unit: "km" })
      return result
    },
    pulseMetrics() {
      const pulse = this.pulse || { average: this.recording?.averageHeartRate, minimum: this.recording?.minimumHeartRate, maximum: this.recording?.maximumHeartRate }
      return [{ label: "Durchschnitt", value: pulse.average }, { label: "Minimum", value: pulse.minimum }, { label: "Maximum", value: pulse.maximum }]
    },
    plot() {
      const recording = this.recording
      if (!recording || !Number.isFinite(Date.parse(recording.startedAt)) || !(Date.parse(recording.endedAt) > Date.parse(recording.startedAt))) return { paths: [], dots: [] }
      return healthPulsePlot(recording)
    },
    hasPulse() { return this.pulseMetrics.some(metric => measured(metric.value) && metric.value > 0) || (Array.isArray(this.recording?.heartRateSamples) && this.recording.heartRateSamples.some(point => measured(point?.bpm) && point.bpm > 0)) },
  },
  methods: { number(value) { return measured(value) ? Math.round(value).toLocaleString("de-CH") : "–" } },
}
</script>
<style scoped>
.session-overview { color: #f4f4f4; }
.session-overview__metrics { display: grid; grid-template-columns: repeat(auto-fit, minmax(100px, 1fr)); gap: 18px 14px; padding: 6px 0 22px; }
.session-overview__metrics > div, .session-overview__pulse-values > div { min-width: 0; }
.session-overview__metrics span, .session-overview__pulse-values span { display: block; color: #aaa; font-size: 11px; line-height: 1.5; margin-bottom: 7px; }
.session-overview strong { display: block; font-size: 25px; font-weight: 600; line-height: 1.3; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
.session-overview strong small { margin-left: 4px; font-size: 11px; font-weight: 400; color: #aaa; white-space: nowrap; }
.session-overview__pulse { padding: 20px 0 0; border-top: 1px solid #ffffff12; }
.session-overview__pulse header { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 20px; }
.session-overview h4 { margin: 0; font-size: 16px; font-weight: 600; }
.session-overview__pulse-values { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 12px; }
.session-overview__pulse-values strong { font-size: 22px; }
.session-overview__chart { margin-top: 20px; color: #ca919b; }
.session-overview__chart svg { display: block; width: 100%; }
.session-overview__chart polyline { fill: none; stroke: currentColor; stroke-width: 1.7; stroke-linecap: round; stroke-linejoin: round; }
.session-overview__chart circle { fill: currentColor; }
.session-overview__axis { display: flex; justify-content: space-between; color: #999; font-size: 10px; margin-top: 6px; }
.session-overview__note { color: #aaa; font-size: 12px; line-height: 1.6; margin-top: 16px; }
.session-overview__details { margin-top: 14px; }
.session-overview__details summary { display: flex; align-items: center; justify-content: space-between; min-height: 48px; color: #ccc; font-size: 12px; cursor: pointer; list-style: none; }
.session-overview__details summary::-webkit-details-marker { display: none; }
.session-overview__details summary .v-icon { color: #999; }
.session-overview__details[open] > summary .v-icon { transform: rotate(180deg); }
.session-overview__details summary:focus-visible { outline: 2px solid var(--v-primary-base, #00963f); outline-offset: 3px; }
.session-overview__details ::v-deep .workout-pulse { margin-top: 0; padding: 12px 0 0; background: transparent; border: 0; border-radius: 0; }
@media (max-width: 360px) { .session-overview__pulse-values { gap: 8px; }.session-overview__pulse-values strong { font-size: 20px; }.session-overview__pulse-values small { display: block; margin: 3px 0 0; } }
</style>
